import { List } from 'immutable';
import { NodeType, ParseOptions, Schema } from 'hubspot-prosemirror/pm/model';
import { EditorState, Plugin } from 'hubspot-prosemirror/pm/state';
import { fontPluginKey } from './plugins/FontPlugin';
import { FocusPlugin } from './plugins/FocusPlugin';
import { createDefaultFontStyleString } from './utils/fontUtils';
import { findAllNodes, findNode } from './utils/nodeUtils';
import { SuggestionItem } from '../types';
import { getDomParser } from './utils/domparser';
import {
  PlaintextOptions,
  convertToPlaintextState,
} from './utils/plaintextUtils';
import { buildDefaultRTEConfig } from './utils/configUtils';
import { HubSpotDOMSerializer } from './utils/domSerializer';

/**
 * Convert the editor state to an HTML string.
 *
 * This function uses the `toDOM` method of each schema node to produce HTML.
 *
 * This function will remove any nodes with the `data-pm-elide` attribute
 * before returning the HTML string.
 *
 * If you wish to exclude a node from the HTML output, you can add the
 * `data-pm-elide` attribute to its `toDOM` output specification.
 */
export function convertToHtml(editorState: EditorState) {
  const div = document.createElement('div');
  let defaultFontStyleString: string;

  // DefaultFont Block
  const fontPluginState = fontPluginKey.getState(editorState);

  if (fontPluginState && fontPluginState.defaultFontInfo) {
    const { defaultFontSize, defaultFontStyle, defaultTextColor } =
      fontPluginState.defaultFontInfo;

    defaultFontStyleString = createDefaultFontStyleString({
      defaultFontSize,
      defaultFontStyle,
      defaultTextColor,
    });

    div.setAttribute('style', defaultFontStyleString);
  }

  // set auto direction for RTL languages
  div.setAttribute('dir', 'auto');
  div.setAttribute('data-top-level', 'true');

  const serializer = HubSpotDOMSerializer.fromSchema(editorState.schema);

  /* Empty paragraphs are replaced with `<br>` tags by overriding the
   * `paragraph` node serializer. Why do we do this?
   *
   * Our schema represents each line as a separate paragraph node, with no
   * explicit hard breaks. Paragraphs have no margin or padding, so visual
   * breaks between content are represented as empty paragraph nodes.
   *
   * This creates a problem: in standard HTML, empty `<p>` tags collapse to
   * zero height and become invisible. But when editing in ProseMirror, empty
   * paragraphs appear to work normally. How?
   *
   * ProseMirror automatically inserts invisible `<br class="ProseMirror-trailingBreak">`
   * tags into empty paragraphs during editing to prevent them from collapsing.
   * This makes it possible to place your cursor in empty paragraphs and type.
   * These trailing breaks are part of the editor's internal DOM representation,
   * not part of the document schema.
   *
   * When serializing to HTML output, ProseMirror correctly omits these trailing
   * breaks since they're not part of the actual document content. This means
   * empty paragraph nodes become empty `<p>` tags in the output HTML.
   *
   * Since our paragraphs have no block-level margins, empty `<p>` tags would
   * collapse and become invisible in the final output, which isn't what we want.
   * We need empty paragraphs to maintain their visual line breaks.
   *
   * Our solution: override the paragraph serializer to convert empty paragraphs
   * into `<br>` tags instead of `<p>` tags. A `<br>` tag always creates a line
   * break and maintains the visual spacing we expect.
   *
   * The corresponding parser rule converts standalone `<br>` tags back into
   * empty paragraph nodes, making serialization and parsing inverse operations.
   *
   * A paragraph is considered "empty" if it contains only text nodes with
   * whitespace-only content.
   *
   * One notable callout: it's common to see `<p><br></p>` representing a blank
   * line in HTML that came from Draft.js. The default HTML parser interprets
   * this as `paragraph > hard_break` nodes. This is a valid way to represent
   * a blank line, and will not be mutated by this serializer.
   */
  serializer.addNodeModifier('paragraph', (node, initialSpec) => {
    if (Array.isArray(initialSpec)) {
      for (let i = 0; i < node.childCount; i++) {
        const child = node.child(i);
        if (child.type.name !== 'text') {
          return initialSpec;
        }
      }
      if (node.textContent?.trim() === '') {
        return ['p', initialSpec[1], ['br', { class: 'hs-trailingbreak' }]];
      }
    }
    return initialSpec;
  });

  const fragment = serializer.serializeFragment(editorState.doc.content);

  div.appendChild(fragment);

  // Inline font-size onto signature elements for email client compatibility
  if (fontPluginState && fontPluginState.defaultFontInfo) {
    const { defaultFontSize } = fontPluginState.defaultFontInfo;

    if (defaultFontSize) {
      const signatureElements = div.querySelectorAll('.hs_signature');
      signatureElements.forEach((signatureDiv) => {
        const elements = signatureDiv.querySelectorAll(
          'p, div, span, h1, h2, h3, h4, h5, h6, li'
        );
        elements.forEach((el) => {
          const existingStyle = (el as HTMLElement).getAttribute('style') || '';
          const hasFontSize = /font-size\s*:/i.test(existingStyle);

          if (!hasFontSize) {
            const newStyle = `font-size: ${defaultFontSize}${
              existingStyle ? `; ${existingStyle}` : ''
            }`;
            (el as HTMLElement).setAttribute('style', newStyle);
          }
        });
      });
    }
  }

  div.querySelectorAll('[data-pm-output="none"]').forEach((el) => {
    el.remove();
  });

  return div.outerHTML;
}

export function convertToPlaintext(
  editorState: EditorState,
  options?: PlaintextOptions
) {
  const plainState = convertToPlaintextState(editorState, options);
  return plainState.doc.textBetween(0, plainState.doc.content.size, '\n');
}

export function hasContent(editorState: EditorState) {
  // checks for content fragment size (minus two for boundaries of Node)
  return !!(editorState.doc.content.size - 2);
}

export function isUploadingImages(editorState: EditorState) {
  return !!findNode(
    editorState.doc,
    (n) => n.type === editorState.schema.nodes.image && n.attrs.isTemporary
  );
}

export function convertToJSON(editorState: EditorState) {
  return editorState.toJSON();
}

export function getEditorStateFromJSON(
  json: { [key: string]: any },
  customPlugins?: Array<Plugin>
) {
  return EditorState.fromJSON(buildDefaultRTEConfig(customPlugins), json);
}

export function getEditorStateFromHtml(
  htmlContent: string,
  customPlugins?: Plugin[],
  options?: Parameters<typeof getDocNodeFromHtml>[1],
  schema?: Schema
) {
  const docNode = getDocNodeFromHtml(htmlContent, options, schema);
  return EditorState.create({
    doc: docNode,
    ...buildDefaultRTEConfig(customPlugins),
  });
}

const BLOCK_CHILD_PARENTS = new Set([
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TBODY',
  'THEAD',
  'TFOOT',
  'TR',
  'DL',
]);

function normalizeWhitespace(node: Node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentNode;
    if (parent) {
      // Inside elements that only accept block children (lists, tables),
      // whitespace-only text nodes are insignificant formatting.
      // Converting their newlines to <br> injects phantom empty list
      // items when parsing third-party HTML.
      if (
        BLOCK_CHILD_PARENTS.has(parent.nodeName) &&
        node.textContent != null &&
        node.textContent.trim() === ''
      ) {
        return;
      }
      const textParts = node?.textContent?.split('\n') || [];
      if (textParts.length > 0 && parent) {
        for (let index = 0; index < textParts.length; index++) {
          const textNode = document.createTextNode(textParts[index]);
          parent.insertBefore(textNode, node);

          if (index < textParts.length - 1) {
            parent.insertBefore(
              document.createElement('br'),
              textNode?.nextSibling
            );
          }
        }
        parent.removeChild(node);
      }
    }
  }
  const whiteSpaceStyle = (node as HTMLElement)?.style?.whiteSpace;
  const nodeRespectsNewLines =
    ['pre', 'pre-wrap', 'nowrap'].includes(whiteSpaceStyle) ||
    node.nodeName === 'PRE';
  const isSignature =
    node.nodeName === 'DIV' &&
    !!(node as HTMLDivElement).getAttribute('data-hs-signature');
  const parseAsMark = node.nodeName === 'A'; // Marks that should not have hard breaks normalized
  const shouldNormalizeWhitespaceInChildren =
    !nodeRespectsNewLines && !isSignature && !parseAsMark;
  if (shouldNormalizeWhitespaceInChildren) {
    let targetNode: ChildNode | null = node.childNodes[0];
    while (targetNode) {
      const nextSibling = targetNode.nextSibling as ChildNode | null;
      normalizeWhitespace(targetNode);
      targetNode = nextSibling;
    }
  }
}

export function getDocNodeFromHtml(
  htmlContent: string,
  options: {
    preserveNewlines?: boolean;
    preserveWhitespace?: boolean | 'full';
  } = {},
  schema?: Schema
) {
  const defaultOptions = {
    preserveNewlines: true,
  };
  const mergedOptions = { ...defaultOptions, ...options };
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  if (mergedOptions.preserveNewlines) {
    normalizeWhitespace(doc.body);
  }

  // Pass preserveWhitespace option to ProseMirror parser
  const parseOptions: ParseOptions = {};
  if (mergedOptions.preserveWhitespace !== undefined) {
    parseOptions.preserveWhitespace = mergedOptions.preserveWhitespace;
  }

  return getDomParser(schema).parse(doc.body, parseOptions);
}

export function focusEditor(editorState: EditorState, focusViewPos?: number) {
  return editorState.apply(
    editorState.tr.setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos,
    })
  );
}

export function getAtMentions(editorState: EditorState) {
  const mentions = findAllNodes(
    editorState.doc,
    (node) => node.type === editorState.schema.nodes.atmention
  );
  let mentionIds = List<number>();
  mentions.forEach((mention) => {
    const ownerId = parseInt(mention.node.attrs.id, 10);
    if (ownerId && !isNaN(ownerId) && !mentionIds.has(ownerId)) {
      mentionIds = mentionIds.push(ownerId);
    }
  });
  return mentionIds;
}

export function getNumberOfNodeType(
  editorState: EditorState,
  nodeType: NodeType
) {
  const nodesFound = findAllNodes(
    editorState.doc,
    (node) => node.type === nodeType
  );
  return nodesFound.length;
}

export function getNumberOfImages(editorState: EditorState) {
  return getNumberOfNodeType(editorState, editorState.schema.nodes.image);
}

export function getNumberOfLinks(editorState: EditorState) {
  let numLinks = 0;
  editorState.doc.descendants((node) => {
    if (editorState.schema.marks.link.isInSet(node.marks)) {
      numLinks++;
    }
    return true;
  });
  return numLinks;
}

export type GetMatchResultsOptions = {
  sortAlphabetically?: boolean;
};

function sortByName(items: SuggestionItem[]): SuggestionItem[] {
  return [...items].sort((a, b) => {
    const nameA = (a.name || a.searchText || '').toLowerCase();
    const nameB = (b.name || b.searchText || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

/**
 * Filters and optionally sorts suggestion items based on a search query.
 *
 * Results are categorized into two groups:
 * 1. "Starts with" matches - items where the name/searchText begins with the query
 * 2. "Contains" matches - items where the query appears elsewhere in the name/searchText
 *
 * "Starts with" matches are always returned before "contains" matches.
 *
 * @param query - The search string to match against
 * @param data - Array of suggestion items to filter
 * @param options.sortAlphabetically - When true, sorts each group alphabetically by name.
 *   When false or omitted, preserves insertion order within each group.
 */
export function getMatchResults(
  query: string,
  data: SuggestionItem[],
  options?: GetMatchResultsOptions
) {
  const exactRegex = new RegExp(
    `^${query.trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}`,
    'i'
  );
  const globalRegex = new RegExp(
    query.trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
    'i'
  );

  const matchResults = data.reduce(
    (acc, item: SuggestionItem) => {
      const toSearch = item.searchText || item.name || '';
      const isExactMatch = toSearch.match(exactRegex) !== null;
      if (isExactMatch) {
        return {
          ...acc,
          exactMatches: [...acc.exactMatches, item],
        };
      }
      const isGlobalMatch = toSearch.match(globalRegex) !== null;
      if (isGlobalMatch) {
        return {
          ...acc,
          globalMatches: [...acc.globalMatches, item],
        };
      }
      return acc;
    },
    {
      exactMatches: [] as SuggestionItem[],
      globalMatches: [] as SuggestionItem[],
    }
  );

  const exactMatches = options?.sortAlphabetically
    ? sortByName(matchResults.exactMatches)
    : matchResults.exactMatches;

  const globalMatches = options?.sortAlphabetically
    ? sortByName(matchResults.globalMatches)
    : matchResults.globalMatches;

  return [...exactMatches, ...globalMatches];
}
